import { ContentCopy } from "@mui/icons-material";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { isNil } from "lodash";
import React, { useEffect, useState } from "react";
import {
  type AdhocPreviewResultQuery,
  IngestionTestRunMode,
  IngestionTestRunState,
  useIngestionTestRunQuery,
} from "../../generated/graphql";
import AdhocDecisionPanel from "../ingestion-adhoc-preview/adhoc-decision-panel";
import AdhocUsageLine from "../ingestion-adhoc-preview/adhoc-usage-line";
import {
  type AppliedResult,
  appliedDecision,
  normalizePreviewPlans,
  pageRangeLabel,
  previewPlanView,
} from "./applied-decision";
import CollapsibleJson from "./collapsible-json";
import IngestionTestRunTimeline from "./ingestion-test-run-timeline";

/**
 * The apply-plan shape rendered by {@link AdhocDecisionPanel}. A test run stores
 * its plan as an opaque JSON blob, but it is the SAME `planV3Apply` output the
 * ad-hoc preview renders (identical camelCase keys), so the blob is cast to this
 * shape rather than forking the panel.
 */
type ApplyPlan = NonNullable<
  AdhocPreviewResultQuery["adhocPreviewResult"]["applyPlan"]
>;

const POLL_INTERVAL_MS = 2000;

const TERMINAL_STATES: ReadonlySet<IngestionTestRunState> = new Set([
  IngestionTestRunState.Completed,
  IngestionTestRunState.Failed,
  IngestionTestRunState.Cleaned,
]);

const styles = {
  paper: { p: 2 },
  statusRow: { p: 2 },
  mono: { fontFamily: "monospace" },
  correlationRow: { mt: 1 },
} satisfies Record<string, SxProps<Theme>>;

const stateColor = (
  state: IngestionTestRunState,
): "success" | "error" | "info" | "default" => {
  if (state === IngestionTestRunState.Completed) return "success";
  if (state === IngestionTestRunState.Failed) return "error";
  if (state === IngestionTestRunState.Cleaned) return "default";
  return "info";
};

/** A copy-to-clipboard button that briefly confirms the copy. */
const CopyButton = ({ value }: { readonly value: string }) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => {
      setCopied(false);
    }, 1500);
    return () => {
      clearTimeout(timer);
    };
  }, [copied]);
  return (
    <Tooltip title={copied ? "Copied" : "Copy"}>
      <IconButton
        size="small"
        onClick={() => {
          void navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
          });
        }}
      >
        <ContentCopy fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
};

/** A `label: <mono id>` row with a copy button, or nothing when the id is nil. */
const LabeledId = ({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
}) =>
  isNil(value) ? null : (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={styles.mono}>
        {value}
      </Typography>
      <CopyButton value={value} />
    </Stack>
  );

/**
 * The authoritative apply outcome for a FULL_APPLY run: one card per applied
 * `scanned_order_result`, showing whether it CREATEd or DEDUP'd, the created /
 * matched order uuid as a copyable value (a copy affordance, not a navigable
 * link — this internal tool has no main-app order route), and the executed apply
 * plan (reusing the ad-hoc decision panel — it is the same `planV3Apply` shape).
 * Before the apply lands the list is empty; the copy then distinguishes "still
 * applying" from a genuinely resultless run rather than implying a planning
 * failure.
 *
 * A multi-shipment file auto-splits into one result per shipment, and the page
 * bounds are the only thing that tells those cards apart — so they are labelled
 * whenever there is more than one.
 */
const FullApplyOutcome = ({
  results,
  active,
}: {
  readonly results: readonly AppliedResult[];
  readonly active: boolean;
}) => {
  if (results.length === 0) {
    return (
      <Alert severity="info">
        {active
          ? "Applying — the authoritative results will appear once the run completes."
          : "No applied results were recorded for this run."}
      </Alert>
    );
  }
  return (
    <Stack spacing={2}>
      {results.map((result) => {
        const decision = appliedDecision(result);
        return (
          <Paper key={result.resultUuid} variant="outlined" sx={styles.paper}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Chip
                size="small"
                label={decision.label}
                color={decision.color}
              />
              <Chip size="small" variant="outlined" label={result.status} />
              {results.length > 1 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={pageRangeLabel(result)}
                />
              )}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={styles.mono}
              >
                {result.resultUuid}
              </Typography>
              <CopyButton value={result.resultUuid} />
            </Stack>
            <Stack spacing={0.5} sx={styles.correlationRow}>
              <LabeledId label="created order" value={result.orderUuid} />
              <LabeledId
                label="duplicate of order"
                value={result.duplicateOfOrderUuid}
              />
            </Stack>
            {!isNil(result.applyPlan) && (
              <AdhocDecisionPanel applyPlan={result.applyPlan as ApplyPlan} />
            )}
          </Paper>
        );
      })}
    </Stack>
  );
};

/**
 * The PREVIEW_E2E outcome: the DRY decision plan once captured, a
 * planning-in-progress note while the run is still extracting, or the
 * failed-before-planning note reserved for a terminal run that produced no plan
 * — so a run mid-extraction is never mislabelled as a planning failure.
 *
 * A test document may hold more than one shipment, in which case the run stores
 * ONE plan per auto-split slice; each is rendered under the pages it covers.
 * {@link normalizePreviewPlans} also reads the bare-object blob rows stored
 * before auto-split, so an existing run still renders its single plan.
 */
const PreviewOutcome = ({
  applyPlan,
  active,
}: {
  readonly applyPlan: unknown;
  readonly active: boolean;
}) => {
  const slices = normalizePreviewPlans(applyPlan);
  const view = previewPlanView({ active, hasPlan: slices.length > 0 });
  if (view === "plan") {
    return (
      <Stack spacing={2}>
        {slices.map((slice) => (
          <Stack key={slice.key} spacing={0.5}>
            {/* Only labelled once the file was actually split — a whole-file
                run has nothing to disambiguate. */}
            {slices.length > 1 && (
              <Chip
                size="small"
                variant="outlined"
                label={pageRangeLabel(slice)}
              />
            )}
            {isNil(slice.plan) ? (
              <Alert severity="warning">
                {`No decision plan recorded for ${pageRangeLabel(slice)}.`}
              </Alert>
            ) : (
              <AdhocDecisionPanel applyPlan={slice.plan as ApplyPlan} />
            )}
          </Stack>
        ))}
      </Stack>
    );
  }
  return (
    <Alert severity="info">
      {view === "planning"
        ? "Planning — the decision plan will appear once the run completes."
        : "No decision plan recorded — the run may have failed before planning."}
    </Alert>
  );
};

/**
 * The debug pane for one Track-2 ingestion test run. Polls while the run is
 * still in flight, then renders its lifecycle timeline, the dispatch and echoed
 * context bags, the persisted decision plan (reusing the ad-hoc decision panel),
 * the exemplar marker, and the cost/usage summary. Read strictly by run id —
 * there is no list view.
 *
 * @param props.runId The ingestion test run id from the route.
 */
const IngestionTestRunDetail = ({ runId }: { readonly runId: string }) => {
  const { data, loading, error, startPolling, stopPolling, refetch } =
    useIngestionTestRunQuery({
      variables: { id: runId },
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    });
  const run = data?.ingestionTestRun;
  const active = run !== undefined && !TERMINAL_STATES.has(run.state);

  useEffect(() => {
    if (active) {
      startPolling(POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [active, startPolling, stopPolling]);

  if (run === undefined) {
    if (error !== undefined) {
      return (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void refetch();
              }}
            >
              Retry
            </Button>
          }
        >
          Failed to load run: {error.message}
        </Alert>
      );
    }
    if (loading) {
      return (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={styles.statusRow}
        >
          <CircularProgress size={18} />
          <Typography>Loading run…</Typography>
        </Stack>
      );
    }
    return <Typography sx={styles.statusRow}>Run not found.</Typography>;
  }

  // The §5.1 test execution context DE echoes back is (run id, mode); the row is
  // the source of truth for both, so it is reconstructed here rather than stored.
  const testExecutionContext = { run_id: run.id, mode: run.mode };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={styles.paper}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="h6">Run</Typography>
          <Chip size="small" label={run.state} color={stateColor(run.state)} />
          <Chip size="small" variant="outlined" label={run.mode} />
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={styles.mono}>
              {run.id}
            </Typography>
            <CopyButton value={run.id} />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            company {run.companyUuid}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {run.actorEmail}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={styles.correlationRow}
        >
          <Typography variant="body2" color="text.secondary">
            correlation id
          </Typography>
          <Typography variant="body2" sx={styles.mono}>
            {run.correlationId ?? "—"}
          </Typography>
          {!isNil(run.correlationId) && (
            <CopyButton value={run.correlationId} />
          )}
        </Stack>
      </Paper>

      {run.state === IngestionTestRunState.Failed && !isNil(run.error) && (
        <Alert severity="error">{run.error}</Alert>
      )}
      {run.state === IngestionTestRunState.Cleaned && !isNil(run.error) && (
        <Alert severity="warning">{run.error}</Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={styles.paper}>
            <Typography gutterBottom variant="h6">
              Lifecycle
            </Typography>
            <IngestionTestRunTimeline
              state={run.state}
              mode={run.mode}
              createdAt={run.createdAt as string}
              updatedAt={run.updatedAt as string}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Stack spacing={2}>
            {run.mode === IngestionTestRunMode.FullApply ? (
              <FullApplyOutcome results={run.appliedResults} active={active} />
            ) : (
              <PreviewOutcome
                applyPlan={run.applyPlan as unknown}
                active={active}
              />
            )}
            <AdhocUsageLine usage={run.usage as unknown} />
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" sx={styles.paper}>
            <Typography gutterBottom variant="h6">
              Context
            </Typography>
            <CollapsibleJson
              title="Dispatch context — email context"
              value={run.emailContext as unknown}
            />
            <CollapsibleJson
              title="Dispatch context — test execution context"
              value={testExecutionContext}
            />
            <CollapsibleJson
              defaultExpanded
              title="Echoed context — extracted fields"
              value={run.extractedFields as unknown}
            />
            <CollapsibleJson
              title="Echoed context — exemplar marker"
              value={run.exemplarMarker as unknown}
            />
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default IngestionTestRunDetail;
